import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as workflowApi from '../services/workflowService';
import type { CreateWorkflowData, UpdateWorkflowData, ApplyWorkflowData, StatusInput, SaveTransitionsData } from '../services/workflowService';
import { useOrgStore } from '../store/orgStore';

export const workflowKeys = {
  all: (orgId?: string | null) => ['workflows', orgId ?? 'none'] as const,
  detail: (id: string) => ['workflows', 'detail', id] as const,
  transitions: (id: string) => ['workflows', 'detail', id, 'transitions'] as const,
};

export function useWorkflows() {
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  return useQuery({
    queryKey: workflowKeys.all(activeOrgId),
    queryFn: workflowApi.fetchWorkflows,
  });
}

export function useWorkflow(workflowId: string) {
  return useQuery({
    queryKey: workflowKeys.detail(workflowId),
    queryFn: () => workflowApi.fetchWorkflow(workflowId),
    enabled: !!workflowId,
  });
}

export function useCreateWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWorkflowData) => workflowApi.createWorkflow(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
}

export function useUpdateWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { workflowId: string; data: UpdateWorkflowData }) =>
      workflowApi.updateWorkflow(params.workflowId, params.data),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(vars.workflowId) });
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
}

export function useValidateWorkflowUpdate() {
  return useMutation({
    mutationFn: (params: { workflowId: string; data: { statuses: StatusInput[] } }) =>
      workflowApi.validateWorkflowUpdate(params.workflowId, params.data),
  });
}

export function useApplyWorkflowUpdate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { workflowId: string; data: ApplyWorkflowData }) =>
      workflowApi.applyWorkflowUpdate(params.workflowId, params.data),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(vars.workflowId) });
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
}

export function usePreviewWorkflowChange() {
  return useMutation({
    mutationFn: (params: { projectId: string; targetWorkflowId: string }) =>
      workflowApi.previewWorkflowChange(params.projectId, params.targetWorkflowId),
  });
}

export function useMigrateProjectWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      projectId: string;
      data: { targetWorkflowId: string; statusMapping: { fromStatusId: string; toStatusId: string }[] };
    }) => workflowApi.migrateProjectWorkflow(params.projectId, params.data),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', vars.projectId] });
    },
  });
}

export function useDeleteWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (workflowId: string) => workflowApi.deleteWorkflow(workflowId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
}

// --- Workflow Transitions (CHG-043) ---

export function useWorkflowTransitions(workflowId: string) {
  return useQuery({
    queryKey: workflowKeys.transitions(workflowId),
    queryFn: () => workflowApi.fetchWorkflowTransitions(workflowId),
    enabled: !!workflowId,
  });
}

export function useSaveWorkflowTransitions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { workflowId: string; data: SaveTransitionsData }) =>
      workflowApi.saveWorkflowTransitions(params.workflowId, params.data),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.transitions(vars.workflowId) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(vars.workflowId) });
    },
  });
}

// --- IssueType Workflow Mapping (CHG-042) ---

export const issueTypeMappingKeys = {
  detail: (projectId: string) => ['issueTypeWorkflowMappings', projectId] as const,
};

export function useIssueTypeWorkflowMappings(projectId: string) {
  return useQuery({
    queryKey: issueTypeMappingKeys.detail(projectId),
    queryFn: () => workflowApi.fetchIssueTypeWorkflowMappings(projectId),
    enabled: !!projectId,
  });
}

export function useSetIssueTypeWorkflowMappings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { projectId: string; mappings: { issueType: string; workflowId: string }[] }) =>
      workflowApi.setIssueTypeWorkflowMappings(params.projectId, params.mappings),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: issueTypeMappingKeys.detail(vars.projectId) });
    },
  });
}

export function usePreviewIssueTypeWorkflowChange() {
  return useMutation({
    mutationFn: (params: { projectId: string; mappings: { issueType: string; workflowId: string }[] }) =>
      workflowApi.previewIssueTypeWorkflowChange(params.projectId, params.mappings),
  });
}

export function useMigrateIssueTypeWorkflows() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      projectId: string;
      data: {
        mappings: { issueType: string; workflowId: string }[];
        statusMigrations: {
          issueType: string;
          statusMapping: { fromStatusId: string; toStatusId: string }[];
        }[];
      };
    }) => workflowApi.migrateIssueTypeWorkflows(params.projectId, params.data),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: issueTypeMappingKeys.detail(vars.projectId) });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', vars.projectId] });
    },
  });
}
